import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cx } from './ui.tsx';

/** Stor framstegsring med norrskensgradient som fylls på när sidan visas. */
export function AuroraRing({
  percent,
  size = 168,
  label = 'klart',
}: {
  percent: number;
  size?: number;
  label?: string;
}) {
  const id = useId().replace(/:/g, '');
  const stroke = Math.round(size / 13);
  const r = (size - stroke) / 2 - 4;
  const c = 2 * Math.PI * r;
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(() => setShown(percent), 80);
    return () => window.clearTimeout(t);
  }, [percent]);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`${percent} procent ${label}`}
      >
        <defs>
          <linearGradient id={`g${id}`} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#2e8b6e" />
            <stop offset="0.55" stopColor="#3fb68b" />
            <stop offset="1" stopColor="#a48cf0" />
          </linearGradient>
          <filter id={`f${id}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation={stroke / 2.2} result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={stroke}
        />
        <circle
          className="ring-arc"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={`url(#g${id})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.max(0.001, shown) / 100)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          filter={`url(#f${id})`}
        />
      </svg>
      <div
        className="absolute inset-0 flex flex-col items-center justify-center text-white"
        aria-hidden="true"
      >
        <span className="text-4xl font-extrabold tabular-nums tracking-tight">{percent}%</span>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">{label}</span>
      </div>
    </div>
  );
}

/** Liten ring för en modul (ljus bakgrund). */
export function MiniRing({
  percent,
  size = 44,
  children,
}: {
  percent: number;
  size?: number;
  children?: ReactNode;
}) {
  const id = useId().replace(/:/g, '');
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <defs>
          <linearGradient id={`m${id}`} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#2e8b6e" />
            <stop offset="1" stopColor="#7a5cc7" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e6ecf7" strokeWidth={stroke} />
        <circle
          className="ring-arc"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={`url(#m${id})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - percent / 100)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-night-800">
        {children}
      </div>
    </div>
  );
}

/** Sexkantigt märke för en modul eller för intyget. */
export function Badge({
  earned,
  label,
  number,
  size = 64,
  kind = 'module',
  icon,
  hint,
}: {
  earned: boolean;
  label: string;
  number?: number;
  size?: number;
  kind?: 'module' | 'certificate' | 'achievement';
  /** SVG-sökväg (24×24) för märken som inte är moduler. */
  icon?: string;
  /** Visas under namnet, t.ex. hur långt man kommit. */
  hint?: string;
}) {
  const id = useId().replace(/:/g, '');
  const hex = 'M32 3 58 18v28L32 61 6 46V18z';
  return (
    <div
      className={cx('flex flex-col items-center gap-1.5 text-center', earned && 'pop-in')}
      style={{ width: size + 24 }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        role="img"
        aria-label={`${label}${earned ? ' – taget' : ' – inte taget än'}`}
      >
        <defs>
          <linearGradient id={`b${id}`} x1="0" y1="1" x2="1" y2="0">
            {kind === 'certificate' ? (
              <>
                <stop offset="0" stopColor="#c58b16" />
                <stop offset="1" stopColor="#ffd56b" />
              </>
            ) : kind === 'achievement' ? (
              <>
                <stop offset="0" stopColor="#5b3fb0" />
                <stop offset="1" stopColor="#3fb68b" />
              </>
            ) : (
              <>
                <stop offset="0" stopColor="#1f3b73" />
                <stop offset="0.5" stopColor="#2e8b6e" />
                <stop offset="1" stopColor="#7a5cc7" />
              </>
            )}
          </linearGradient>
        </defs>
        <path
          d={hex}
          fill={earned ? `url(#b${id})` : '#eef1f7'}
          stroke={earned ? 'rgba(255,255,255,0.6)' : '#d5dbe8'}
          strokeWidth="2"
        />
        {kind === 'achievement' ? (
          <g transform="translate(17 17) scale(1.25)">
            <path
              d={icon}
              fill="none"
              stroke={earned ? '#fff' : '#b3bdd0'}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        ) : kind === 'certificate' ? (
          <path
            d="M32 17l4.1 8.4 9.2 1.3-6.7 6.5 1.6 9.2L32 38.1l-8.2 4.3 1.6-9.2-6.7-6.5 9.2-1.3z"
            fill={earned ? '#fff' : '#c9d1e0'}
          />
        ) : earned ? (
          <>
            <path
              d="M32 14l2.6 6.4L41 23l-6.4 2.6L32 32l-2.6-6.4L23 23l6.4-2.6z"
              fill="#fff"
              opacity="0.95"
            />
            <text x="32" y="47" textAnchor="middle" fontSize="15" fontWeight="800" fill="#fff">
              {number}
            </text>
          </>
        ) : (
          <text x="32" y="39" textAnchor="middle" fontSize="18" fontWeight="800" fill="#b3bdd0">
            {number}
          </text>
        )}
      </svg>
      <span
        className={cx(
          'line-clamp-2 text-[11px] leading-tight',
          earned ? 'font-semibold text-night-800' : 'text-muted',
        )}
      >
        {label}
      </span>
      {hint && <span className="-mt-1 text-[11px] tabular-nums text-muted">{hint}</span>}
    </div>
  );
}

export interface JourneyStop {
  id: number | string;
  label: string;
  done: boolean;
  current: boolean;
  locked?: boolean;
  /** Hur långt deltagaren kommit i den pågående stationen (0–1). */
  fraction?: number;
}

/** Kursen som en resa: en station per modul och intyget som mål. */
export function Journey({
  stops,
  onSelect,
}: {
  stops: JourneyStop[];
  onSelect?: (stop: JourneyStop) => void;
}) {
  const lastDone = stops.reduce((acc, s, i) => (s.done ? i : acc), -1);
  const currentIndex = stops.findIndex((s) => s.current);
  const position = currentIndex >= 0 ? currentIndex + (stops[currentIndex].fraction ?? 0) : lastDone;
  const fill = stops.length > 1 ? Math.max(0, Math.min(1, position / (stops.length - 1))) : 0;
  return (
    <div className="-mx-2 overflow-x-auto px-2 pb-2">
      <ol
        className="relative flex min-w-[30rem] items-start justify-between"
        aria-label="Din resa genom kursen"
      >
        <div className="absolute left-6 right-6 top-6 h-1.5 rounded-full bg-night-100" aria-hidden="true" />
        <div
          className="absolute left-6 top-6 h-1.5 rounded-full bg-gradient-to-r from-aurora-500 via-aurora-400 to-violet-aurora transition-all duration-1000"
          style={{ width: `calc((100% - 3rem) * ${fill})` }}
          aria-hidden="true"
        />
        {stops.map((s, i) => {
          const last = i === stops.length - 1;
          return (
            <li key={s.id} className="relative z-10 flex w-20 flex-col items-center text-center sm:w-24">
              <button
                type="button"
                onClick={() => onSelect?.(s)}
                disabled={s.locked}
                className={cx(
                  'flex h-12 w-12 items-center justify-center rounded-full border-4 border-white text-base font-extrabold shadow-md transition',
                  s.done && !last && 'bg-gradient-to-br from-aurora-500 to-violet-aurora text-white',
                  s.done && last && 'bg-gradient-to-br from-amber-500 to-yellow-300 text-white',
                  !s.done && s.current && 'glow-pulse bg-white text-aurora-600 ring-2 ring-aurora-400',
                  !s.done && !s.current && 'bg-night-50 text-night-700/60',
                  !s.locked && 'hover:scale-110',
                )}
                aria-label={`${s.label}${s.done ? ', klar' : s.current ? ', pågår' : s.locked ? ', låst' : ''}`}
              >
                {last ? '★' : s.done ? '✓' : s.locked ? '🔒' : i + 1}
              </button>
              <span
                className={cx(
                  'mt-2 line-clamp-2 text-xs leading-tight',
                  s.current ? 'font-bold text-night-900' : 'text-muted',
                )}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Konfetti i norrskensfärger. Respekterar inställningen för minskad rörelse. */
export function Confetti({ fire }: { fire: number }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!fire || !canvas.current) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const el = canvas.current;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = window.innerWidth * dpr;
    el.height = window.innerHeight * dpr;
    ctx.scale(dpr, dpr);
    const colors = ['#3fb68b', '#2e8b6e', '#7a5cc7', '#a48cf0', '#ffd56b', '#ffffff', '#2b4d8f'];
    const pieces = Array.from({ length: 160 }, () => ({
      x: window.innerWidth / 2 + (Math.random() - 0.5) * 200,
      y: window.innerHeight * 0.35,
      vx: (Math.random() - 0.5) * 14,
      vy: -Math.random() * 14 - 4,
      size: 5 + Math.random() * 6,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
    let frame = 0;
    let raf = 0;
    const tick = () => {
      frame++;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (const p of pieces) {
        p.vy += 0.32;
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, 1 - frame / 160);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      }
      if (frame < 160) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fire]);
  return (
    <canvas
      ref={canvas}
      className="pointer-events-none fixed inset-0 z-50 h-full w-full"
      aria-hidden="true"
    />
  );
}

/** Kort bekräftelse i nederkant av skärmen. */
export function Toast({ message, onDone }: { message: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!message) return;
    const t = window.setTimeout(onDone, 3200);
    return () => window.clearTimeout(t);
  }, [message, onDone]);
  if (!message) return null;
  return (
    <div role="status" className="fixed inset-x-0 bottom-6 z-50 flex justify-center px-4">
      <div className="pop-in flex items-center gap-2 rounded-full bg-night-900 px-5 py-3 text-sm font-semibold text-white shadow-2xl ring-1 ring-white/10">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-aurora-500 to-violet-aurora">
          ✓
        </span>
        {message}
      </div>
    </div>
  );
}
